import type { ProjectCreator, ResolvedKeybindingsConfig } from "@t3tools/contracts";
import { ChevronDownIcon, FolderIcon, TriangleAlertIcon } from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/utils";
import type { Project } from "../../types";
import {
  buildProjectActionItems,
  filterCommandPaletteGroups,
  ITEM_ICON_CLASS,
  type CommandPaletteActionItem,
  type CommandPaletteGroup,
  type CommandPaletteSubmenuItem,
} from "../CommandPalette.logic";
import { CommandPaletteResults } from "../CommandPaletteResults";
import { ProjectFavicon } from "../ProjectFavicon";
import { ProjectOwnerAvatar, TitleAuthorTooltip } from "../ProjectOwnerAvatar";
import { badgeVariants } from "../ui/badge";
import { Command, CommandInput, CommandPanel } from "../ui/command";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";

interface DraftProjectChipProps {
  projects: ReadonlyArray<Project>;
  activeProject: Project | undefined;
  /** The owner of `activeProject` on a team server; `null` elsewhere. */
  activeProjectOwner: ProjectCreator | null;
  projectMissing: boolean;
  keybindings: ResolvedKeybindingsConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectProject: (project: Project) => void;
}

/**
 * Header chip for a draft thread: shows the target project and opens a
 * searchable project picker. Prompts for a project while none is chosen and
 * warns when the draft's project no longer exists.
 */
export const DraftProjectChip = memo(function DraftProjectChip({
  projects,
  activeProject,
  activeProjectOwner,
  projectMissing,
  keybindings,
  open,
  onOpenChange,
  onSelectProject,
}: DraftProjectChipProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [highlightedItemValue, setHighlightedItemValue] = useState<string | null>(null);
  const unresolved = activeProject === undefined;

  useEffect(() => {
    if (!open) {
      setQuery("");
      setHighlightedItemValue(null);
    }
  }, [open]);

  const projectItems = useMemo(
    () =>
      buildProjectActionItems({
        projects,
        valuePrefix: "draft-project",
        icon: (project) => (
          <ProjectFavicon
            environmentId={project.environmentId}
            cwd={project.cwd}
            className={ITEM_ICON_CLASS}
          />
        ),
        runProject: async (project) => {
          onSelectProject(project);
        },
      }),
    [onSelectProject, projects],
  );

  const groups = useMemo<CommandPaletteGroup[]>(
    () =>
      filterCommandPaletteGroups({
        activeGroups: [
          { value: "projects", label: t("common/projects_n0igzu"), items: projectItems },
        ],
        query,
        isInSubmenu: true,
        projectSearchItems: [],
        threadSearchItems: [],
      }),
    [projectItems, query, t],
  );

  const executeItem = (item: CommandPaletteActionItem | CommandPaletteSubmenuItem) => {
    if (item.kind !== "action" || item.disabled) {
      return;
    }
    void item.run();
  };

  const triggerLabel = activeProject
    ? activeProject.name
    : projectMissing
      ? t("chat/projectNotSelected_ilxkm9")
      : t("chat/chooseAProject_jd3130");
  // A chosen project's tooltip takes the native title's place: both would show at once.
  const triggerTitle = activeProject
    ? undefined
    : projectMissing
      ? t("chat/theProjectForThis_m90rkk")
      : t("chat/pickTheProjectThis_9f9qoe");

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <TitleAuthorTooltip
        title={activeProject?.name ?? null}
        author={activeProjectOwner}
        side="bottom"
        disabled={open}
        render={
          <PopoverTrigger
            render={
              <button
                type="button"
                data-testid="draft-project-chip"
                data-project-unresolved={unresolved ? "true" : undefined}
                aria-label={
                  unresolved ? t("chat/selectAProject_8qmrv1") : t("chat/changeProject_x9kktv")
                }
                title={triggerTitle}
                className={cn(
                  badgeVariants({ variant: projectMissing ? "warning" : "outline" }),
                  "min-w-0 max-w-full shrink overflow-hidden pe-1 sm:max-w-56",
                  projectMissing && "border-warning/40",
                  unresolved && !projectMissing && "border-dashed text-muted-foreground",
                )}
              />
            }
          >
            {projectMissing ? (
              <TriangleAlertIcon className="text-warning" />
            ) : unresolved ? (
              <FolderIcon />
            ) : activeProjectOwner ? (
              <ProjectOwnerAvatar owner={activeProjectOwner} size="sm" />
            ) : null}
            <span className="min-w-0 truncate">{triggerLabel}</span>
            <ChevronDownIcon className="opacity-60" />
          </PopoverTrigger>
        }
      />
      <PopoverPopup
        align="start"
        className="w-[min(28rem,calc(100vw-1.5rem))] overflow-hidden p-0"
        viewportClassName="p-0"
      >
        <Command
          aria-label={t("chat/selectProject_28ht67")}
          autoHighlight="always"
          mode="none"
          value={query}
          onValueChange={setQuery}
          onItemHighlighted={(value) => {
            setHighlightedItemValue(typeof value === "string" ? value : null);
          }}
        >
          <CommandInput placeholder={t("chat/searchProjects_wvr5j9")} />
          <CommandPanel className="max-h-[min(20rem,50vh)] rounded-b-lg border-b">
            <CommandPaletteResults
              groups={groups}
              highlightedItemValue={highlightedItemValue}
              isActionsOnly={false}
              keybindings={keybindings}
              onExecuteItem={executeItem}
              emptyStateMessage={
                projects.length === 0
                  ? t("chat/addAProjectTo_94aet6")
                  : t("chat/noMatchingProjects_boiczc")
              }
            />
          </CommandPanel>
        </Command>
      </PopoverPopup>
    </Popover>
  );
});
